import PartnershipsService from '../../api/partnerships';
import filteringHelper from 'src/services/filteringHelper';
import _ from 'lodash';
import Vue from 'vue';
const state = {
  partnershipsData: {},
  customGraphData: {
    publication: [],
    patent: [],
  },
  loadState: {
    publication: false,
    patent: false,
  },
  entityData: [],
  countryData: [],
  stateData: [],
  pagination: {
    from: 0,
    page: 1,
    rowsPerPage: 10,
    sort: 'Alphabetical',
    sortBy: 'desc',
    rowsNumber: null,
  },
  heatmapImage: null,
  networkGraphImage: null,
  yearRange: {
    min: new Date().getFullYear() - (5 - 1),
    max: new Date().getFullYear(),
  },
};

const getters = {
  partnershipsData: (state) => {
    return state.partnershipsData;
  },
  entityData: (state) => {
    return state.entityData;
  },
  countryData: (state) => {
    return state.countryData;
  },
  stateData: (state) => {
    return state.stateData;
  },
  customGraphData: (state) => {
    return state.customGraphData;
  },
  loadState: (state) => {
    return state.loadState;
  },
  pagination: (state) => {
    return state.pagination;
  },
  heatmapImage: (state) => {
    return state.heatmapImage;
  },
  networkGraphImage: (state) => {
    return state.networkGraphImage;
  },
  yearRange: (state) => {
    return state.yearRange;
  },
};

const actions = {
  // Fetch partnerships data
  async getPartnershipsData({ commit, dispatch, rootGetters }, payload) {
    // Add year range to state if it exists in the payload
    if (payload && payload.yearRange) {
      dispatch('setYearRange', payload.yearRange);
    }

    // Get scan type
    const scanType = rootGetters['scans/scanType'];

    // Set source for elasticsearch index by what is included in the payload --OR-- whatever the activeDataSource is
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];

    // Update load state to false to indicate data is being loaded
    dispatch('loadState', { source, data: false });

    // Set query_builder to query by default
    let query_builder = payload.query;

    // If any current global filters are applied, add them to payload sent to backend
    let _filters = {};
    if (
      rootGetters['filters/currentFiltering'][source] &&
      Object.keys(rootGetters['filters/currentFiltering'][source]).length
    ) {
      // Use lodash _.cloneDeep to prevent object transferrance
      _filters = _.cloneDeep(rootGetters['filters/currentFiltering'][source]);
    }

    if (_filters) {
      // Process filters based on scan type
      if (scanType !== 'semantic') {
        // Check if search_within_results is included in filters object
        if (_filters.search_within_results) {
          // Call helper function to add search within results to query
          query_builder = filteringHelper.filterResultsText(
            payload.query,
            _filters.search_within_results
          );
        }
      } else {
        // Check if positive or negative filters are included in filters object
        if (_filters.positive || _filters.negative) {
          // Call helper function to add positive and negative filters to query
          query_builder = filteringHelper.filterSemantic(payload.query, {
            positive: _filters.positive,
            negative: _filters.negative,
          });
        }
      }
    }

    payload.query = query_builder; // Update payload query with query_builder
    payload.scan_type = scanType; // Add scan type to payload

    // If the applied global filters include publication year, overwrite them with yearRange in payload -- or add the property if it doesn't already exist.
    if (source === 'publication') {
      _filters.publication_year = [payload.yearRange];
    } else {
      _filters.filing_year = [payload.yearRange];
    }

    // Delete yearRange property since it's now included in filters
    delete payload.yearRange;
    // Create filters property and add _filters content to it
    payload.filters = _filters;

    let partnershipsService = new PartnershipsService();
    const _data = await partnershipsService.loadPartnerships(payload);
    if (!_data.error) {
      commit('setPartnershipsData', {
        ..._data,
        type: payload.type,
        source,
      });
      dispatch('loadState', { source, data: true });
      // Let component know data was returned
      return true;
    } else {
      // Clear data for the source
      commit('setPartnershipsData', {
        source,
      });
      // Update load state
      dispatch('loadState', { source, data: true });
      // Let component know data was not returned
      return false;
    }
  },
  // Fetch entity data for customGraphModal table
  async getEntityData({ commit, dispatch }, payload) {
    let partnershipsService = new PartnershipsService();
    const _data = await partnershipsService.getInstitutions(payload);
    if (!_data.error) {
      commit('setEntityData', _data.institutions);
      dispatch('setPagination', _data.pagination);
    }
  },
  // Fetch countries and states data for customGraphModal table
  async getCountryStateData({ commit }) {
    let partnershipsService = new PartnershipsService();
    const _data = await partnershipsService.getCountriesStates();
    if (!_data.error) {
      const { countries, states } = _data.data;
      commit('setCountryData', countries);
      commit('setStateData', states);
    }
  },
  // Fetch custom graph data for current scan
  async getCustomGraphData({ commit, dispatch }, payload) {
    let partnershipsService = new PartnershipsService();
    const _data = await partnershipsService.getCustomGraphs(payload);

    if (!_data.error) {
      const graphData = _data.data;
      // Commit mutation to load the custom graph data array into state
      commit('setCustomGraphData', { data: graphData, source: payload.source });
      return true;
    } else {
      return false;
    }
  },
  // Call backend to create a custom graph, commit mutation to add new graph to state
  async createCustomGraph({ commit, dispatch }, payload) {
    let partnershipsService = new PartnershipsService();
    const _data = await partnershipsService.createCustomGraph(payload);
    // Service only returns id and error object; package data as store expects with payload from component and id returned from service
    if (!_data.error) {
      const data = {
        id: _data.data[0].id,
        entities: payload.entities,
        entity_type: payload.entityType,
        entity_subtype: payload.entitySubType,
        graph_type: payload.graphType,
        is_universal: payload.isUniversal,
        name: payload.graphName,
        user_id: payload.userId,
      };

      // Commit mutation to add created graph to state
      commit('createCustomGraph', { data, source: payload.source });
      // Let component know request was successful
      return true;
    } else {
      // Let component know request was unsuccessful
      return false;
    }
  },
  // Call backend to edit a custom graph, commit mutation to update state with new values
  async updateCustomGraph({ commit, dispatch }, payload) {
    let partnershipsService = new PartnershipsService();
    const _data = await partnershipsService.editCustomGraph(payload);
    if (!_data.error) {
      // Service only returns error object, package data as store expects with payload from component
      const data = {
        id: payload.graphId,
        entities: payload.entities,
        entity_type: payload.entityType,
        entity_subtype: payload.entitySubType,
        graph_type: payload.graphType,
        is_universal: payload.isUniversal,
        name: payload.graphName,
        user_id: payload.userId,
      };
      // Commit mutation to update the edited graph in state
      commit('updateCustomGraph', { data, source: payload.source });
      // Let component know request was successful
      return true;
    } else {
      // Let component know request was unsuccessful
      return false;
    }
  },
  // Call backend to delete a custom graph, commit mutation to remove graph data from state
  async deleteCustomGraph({ state, commit, dispatch }, payload) {
    let partnershipsService = new PartnershipsService();
    const _data = await partnershipsService.deleteCustomGraph(payload);
    if (!_data.error) {
      // Filter deleted graph from custom graph state
      const filteredCustomGraphData = state.customGraphData[
        payload.source
      ].filter((graph) => graph.id !== payload.graphId);
      // Commit mutation to update state with new graph data
      commit('setCustomGraphData', {
        data: filteredCustomGraphData,
        source: payload.source,
      });
      // Let component know request was successful
      return true;
    } else {
      // Let component know request was unsuccessful
      return false;
    }
  },
  // Call mutation to set load state to true
  loadState({ commit }, payload) {
    commit('setLoadState', payload);
  },
  setPagination({ commit }, payload) {
    commit('setPagination', payload);
  },
  setYearRange({ commit }, payload) {
    commit('setYearRange', payload);
  },
  clearData({ commit }) {
    commit('setPartnershipsData', null);
    commit('setCustomGraphData', null);
    commit('heatmapImage', null);
    commit('networkGraphImage', null);
    commit('setLoadState', false);
  },
};

const mutations = {
  // Load data returned from partnerships service into state
  setPartnershipsData(state, payload) {
    // Add data to state based on source
    if (payload) {
      const _source = payload.source;
      delete payload.source;
      Vue.set(state.partnershipsData, _source, payload);
    } else {
      // Clear state if no payload
      state.partnershipsData = {};
    }
  },
  // Load entities returned from partnerships service into state
  setEntityData(state, payload) {
    state.entityData = payload;
  },
  // Load countries returned from partnerships service into state
  setCountryData(state, payload) {
    state.countryData = payload;
  },
  // Load states returned from partnerships service into state
  setStateData(state, payload) {
    state.stateData = payload;
  },
  // Load custom graph data returned from partnerships service into state
  setCustomGraphData(state, payload) {
    if (!payload) {
      state.customGraphData = {
        publication: [],
        patent: [],
      };
    } else {
      Vue.set(state.customGraphData, payload.source, payload.data);
    }
  },
  // Add newly created graph data to state
  createCustomGraph(state, payload) {
    state.customGraphData[payload.source].push(payload.data);
  },
  // Find edited graph in state by index; update graph state with payload from component
  updateCustomGraph(state, payload) {
    const graph = state.customGraphData[payload.source].find(
      (item) => item.id === payload.data.id
    );
    Object.assign(graph, payload.data);
  },
  // Commit mutation to change load state to true
  setLoadState(state, payload) {
    if (!payload) {
      state.loadState = {
        publication: false,
        patent: false,
      };
    } else {
      // Set loadState for the source
      Vue.set(state.loadState, payload.source, payload.data);
    }
  },
  setPagination(state, payload) {
    state.pagination = { ...payload };
  },
  setYearRange(state, payload) {
    state.yearRange = payload;
  },
  heatmapImage(state, payload) {
    state.heatmapImage = payload;
  },
  networkGraphImage(state, payload) {
    state.networkGraphImage = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
